<template>
  <div class="mt-6">
    <!-- Ratings list header -->
    <div class="flex items-center justify-between mb-6">
      <h2 class="text-xl font-semibold text-white">
        Ocene kao {{ userRole === 'seller' ? 'prodavac' : 'kupac' }}
        <span
          v-if="ratingFilter !== 'all'"
          class="text-sm font-normal text-gray-400"
        >
          ({{ ratingFilter === 'positive' ? 'pozitivne' : 'negativne' }})
        </span>
      </h2>
      <div v-if="meta.total > 0" class="text-sm text-gray-400">
        Ukupno: {{ meta.total }} ocena
      </div>
    </div>

    <!-- Ratings content container with fixed minimum height -->
    <div class="min-h-[600px] flex flex-col">
      <!-- Loader for ratings -->
      <div
        v-if="showLoader"
        class="flex justify-center items-center py-16 flex-grow"
      >
        <div
          class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"
        ></div>
      </div>

      <!-- Ratings list -->
      <div
        v-else-if="!loading && ratings.length > 0"
        class="space-y-6 flex-grow"
      >
        <div
          v-for="rating in ratings"
          :key="rating.id"
          class="bg-gray-800 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow border-l-4"
          :class="{
            'border-green-500': rating.isLike,
            'border-red-500': !rating.isLike,
          }"
        >
          <div class="flex flex-col md:flex-row md:items-start">
            <!-- User avatar -->
            <div class="flex-shrink-0 mb-4 md:mb-0 md:mr-6">
              <div
                class="rounded-full w-12 h-12 flex items-center justify-center text-lg font-bold"
                :class="{
                  'bg-green-100 text-green-800': rating.isLike,
                  'bg-red-100 text-red-800': !rating.isLike,
                }"
              >
                {{ rating.fromUser.username.charAt(0).toUpperCase() }}
              </div>
            </div>

            <!-- Rating content -->
            <div class="flex-grow">
              <!-- Rating header -->
              <div
                class="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4"
              >
                <div>
                  <h3 class="text-lg font-medium text-white mb-1">
                    {{ rating.fromUser.username }}
                  </h3>
                  <div class="flex items-center space-x-3">
                    <div
                      class="px-3 py-1 rounded-full text-sm font-medium flex items-center"
                      :class="{
                        'bg-green-900 text-green-300': rating.isLike,
                        'bg-red-900 text-red-300': !rating.isLike,
                      }"
                    >
                      <span class="mr-1">
                        {{ rating.isLike ? '👍' : '👎' }}
                      </span>
                      {{
                        rating.isLike ? 'Pozitivna ocena' : 'Negativna ocena'
                      }}
                    </div>
                    <span
                      class="text-xs text-gray-500 bg-gray-700 px-2 py-1 rounded"
                    >
                      {{ userRole === 'seller' ? 'Prodaja' : 'Kupovina' }}
                    </span>
                  </div>
                </div>
                <div class="text-sm text-gray-400">
                  {{ formatDate(rating.createdAt) }}
                </div>
              </div>

              <!-- Rating comment -->
              <div v-if="rating.comment" class="mb-4">
                <p class="text-gray-300 leading-relaxed">
                  {{ rating.comment }}
                </p>
              </div>

              <!-- Your reply -->
              <div
                v-if="rating.reply"
                class="mb-4 p-4 bg-gray-700 rounded-lg border-l-4 border-blue-500"
              >
                <div class="flex items-center mb-2">
                  <svg
                    class="w-4 h-4 text-blue-400 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
                    />
                  </svg>
                  <span class="text-sm font-medium text-blue-400"
                    >Vaš odgovor:</span
                  >
                </div>
                <p class="text-gray-300">{{ rating.reply }}</p>
              </div>

              <!-- Product info -->
              <div
                class="flex flex-col md:flex-row md:items-center md:justify-between gap-3 pt-4 border-t border-gray-700"
              >
                <router-link
                  :to="{
                    name: 'product-detail',
                    params: { id: rating.product.slug },
                  }"
                  class="text-blue-400 hover:text-blue-300 transition-colors flex items-center group"
                >
                  <svg
                    class="h-4 w-4 mr-2 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <span class="font-medium">{{ rating.product.name }}</span>
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No ratings message -->
      <div
        v-else-if="!loading"
        class="bg-gray-800 rounded-lg p-12 text-center flex-grow flex items-center justify-center"
      >
        <div class="max-w-md mx-auto">
          <div class="mb-4">
            <div
              class="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
              :class="{
                'bg-blue-900': userRole === 'seller',
                'bg-green-900': userRole === 'buyer',
              }"
            >
              <svg
                class="w-8 h-8"
                :class="{
                  'text-blue-400': userRole === 'seller',
                  'text-green-400': userRole === 'buyer',
                }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
                />
              </svg>
            </div>
          </div>
          <h3 class="text-lg font-medium text-white mb-2">
            Nemate ocene kao {{ userRole === 'seller' ? 'prodavac' : 'kupac' }}
          </h3>
          <p class="text-gray-400 mb-6">
            <span v-if="ratingFilter !== 'all'">
              Nemate
              {{ ratingFilter === 'positive' ? 'pozitivnih' : 'negativnih' }}
              ocena kao {{ userRole === 'seller' ? 'prodavac' : 'kupac' }}.
            </span>
            <span v-else>
              {{
                userRole === 'seller'
                  ? 'Kada prodajete proizvode, kupci će moći da vas ocenjuju.'
                  : 'Kada kupujete proizvode, prodavci će moći da vas ocenjuju.'
              }}
            </span>
          </p>
          <router-link
            :to="userRole === 'seller' ? '/create-product' : '/products'"
            class="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg transition-colors"
          >
            <svg
              class="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                :d="
                  userRole === 'seller'
                    ? 'M12 6v6m0 0v6m0-6h6m-6 0H6'
                    : 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                "
              />
            </svg>
            {{
              userRole === 'seller' ? 'Dodaj novi oglas' : 'Pregledaj proizvode'
            }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Pagination - outside the content container -->
    <div class="mt-8">
      <PaginationComponent
        :meta="meta"
        :current-page="currentPage"
        @page-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, watch, ref, onBeforeUnmount } from 'vue';
import PaginationComponent from '@/components/PaginationComponent.vue';
import { debounce } from '@/utils/debounce';

interface Rating {
  id: string;
  fromUserId: string;
  toUserId: string;
  isLike: boolean;
  comment: string;
  reply: string | null;
  productId: string;
  ratingType: 'seller' | 'buyer';
  createdAt: string;
  updatedAt: string;
  fromUser: {
    id: string;
    username: string;
  };
  product: {
    id: string;
    name: string;
    slug: string;
  };
}

interface Meta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export default defineComponent({
  name: 'MyRatingsList',
  components: {
    PaginationComponent,
  },
  props: {
    ratings: {
      type: Array as PropType<Rating[]>,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    meta: {
      type: Object as PropType<Meta>,
      required: true,
    },
    userRole: {
      type: String as PropType<'seller' | 'buyer'>,
      default: 'seller',
    },
    ratingFilter: {
      type: String as PropType<'all' | 'positive' | 'negative'>,
      default: 'all',
    },
  },
  emits: ['change-page'],
  setup(props, { emit }) {
    const showLoader = ref(false);

    // Debounced loader functions
    const debouncedShowLoader = debounce(() => {
      showLoader.value = true;
    }, 300);

    const hideLoaderImmediately = () => {
      debouncedShowLoader.cancel();
      showLoader.value = false;
    };

    // Watch loading state for debounced spinner
    watch(
      () => props.loading,
      (isLoading) => {
        if (isLoading) {
          debouncedShowLoader();
        } else {
          hideLoaderImmediately();
        }
      }
    );

    const formatDate = (dateString: string): string => {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('sr-RS', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date);
    };

    // Debounced page change function
    const debouncedPageChange = debounce((page: number) => {
      emit('change-page', page);
    }, 200);

    // Clean up on unmount
    onBeforeUnmount(() => {
      debouncedShowLoader.cancel();
    });

    return {
      showLoader,
      formatDate,
      handlePageChange: debouncedPageChange,
    };
  },
});
</script>
